/* iter55z-e — Admin Guide Panel.
 *
 * A comprehensive, admin-only reference documenting how to operate
 * the The Loom + Bookstore + Marketplace platform. Rendered as a
 * static, searchable, sectioned page inside the AdminPage tabs so
 * admins never have to leave the console to look up how to grant a
 * tier, seed a template, rotate the admin password, etc.
 *
 * Kept as inline JSX (not editable via the HelpManualPanel API) so
 * the guide is versioned with the code and cannot drift out of sync
 * with the real behaviour of the app.
 */
import React, { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import {
  Users, Coins, ShieldCheck, Crown, CreditCard, MessageSquare,
  Store, BarChart3, LifeBuoy, Search, ChevronRight, Bell,
} from 'lucide-react';
import { PanelHeader } from './_shared';

const SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: LifeBuoy,
    body: [
      { h: 'Two admin consoles' },
      { p: 'The Loom admin lives at /admin. The Bookstore admin lives at /bookstore/admin. Both use the SAME login credentials, but scopes control which panels each admin sees. An admin with only the `bookstore` scope will not see the The Loom users table even if they log into /admin.' },
      { h: 'Roles vs Scopes' },
      { l: [
        'Roles (Administrator, Store Manager, Editor, Support) control what UI you see.',
        'Scopes (`pro_writer`, `bookstore`, `marketplace`) control which routes the backend allows.',
        'Change either in the "Admin Users" tab.',
      ]},
      { h: 'Sidebar cards' },
      { l: [
        '"View as tier" — impersonate Basic / Pro / Premier as an admin to test feature-gating.',
        '"LLM Cost & Balance" — live COGS via the Emergent Universal Key. Click the bell to set a daily spend alert.',
      ]},
    ],
  },
  {
    id: 'users-tokens',
    title: 'Users & Tokens',
    icon: Users,
    body: [
      { h: 'Grant / revoke tokens' },
      { p: 'Users & Tokens → Type an integer in the ACQUIRE box to top up the admin pool. Then hit "Add to pool". Individual user credits come from that pool. Negative values return tokens.' },
      { h: 'Token math' },
      { l: [
        'The Loom users buy internal tokens via Stripe. Each internal token maps to real LLM output via the Emergent Universal Key.',
        'Default: $1 buys ~60,000 internal tokens (`tokens_per_usd` in app_config). Real Claude Sonnet 4.5 pass-through cost is ~$5 per million output tokens, so gross margin is roughly 10–40 percent on a Sonnet-heavy mix.',
        'Every LLM call writes a row to `usage_events` AND `cost_events`. The Admin sidebar card and Accounting tab both read from `cost_events`.',
      ]},
      { h: 'Archive vs delete' },
      { p: 'The Users table "Reset" action archives the user (moves data to `archived_users`) rather than hard-deleting. Use "Archived Users" to browse or restore.' },
    ],
  },
  {
    id: 'subscriptions',
    title: 'Subscription Tiers',
    icon: Crown,
    body: [
      { h: 'Four tiers' },
      { l: [
        'Basic — signed-in but no paid AI. No draft/build/chat access.',
        'The Loom — full editor + all AI features.',
        'Premier Studio — The Loom + human services (editor review, marketing, cover design). Adds the Premier "Chat with your Studio" surface.',
        'Bookstore — read-only self-serve storefront for print orders.',
      ]},
      { h: 'Grant tier without payment' },
      { p: 'Users → click the tier badge on a user row → pick the new tier. Admins can grant ANY tier (including their own account) without going through Stripe. Grants are audited in `admin_audit`.' },
      { h: 'Impersonate tier' },
      { p: 'Use the "View as tier" card in the sidebar. Reloads the page with the impersonated tier so every capability-gated flow can be validated from one login. Effect is per-browser and clears on logout.' },
    ],
  },
  {
    id: 'pricing-payments',
    title: 'Pricing & Payments',
    icon: CreditCard,
    body: [
      { h: 'Stripe key modes' },
      { l: [
        'Global platform key in `.env` — used unless a user pastes their own key.',
        'User personal key (`users.stripe_api_key`) — for advanced users who want their own dashboard.',
        'Every checkout tags its non-secret key source in the transaction row for operational auditing.',
      ]},
      { h: 'Package pricing' },
      { p: 'Pricing tab lets you edit token top-up packages (Starter / Pro / Power) and Copyscape packs. Token packs: Starter $10 (50K tokens), Pro $40 (250K tokens), Power $120 (1M tokens). Canva is now BYO — users connect their own Canva account. Changes take effect immediately for new checkout sessions.' },
      { h: 'Stripe Connect (author payouts)' },
      { l: [
        'Authors onboard via Stripe Express — Admin → Connect Payouts tab.',
        'iter55g33: Direct Checkout Application Fees route the platform 15% at charge time via `payment_intent_data.application_fee_amount`; the author 85% lands on their connected account instantly. Zero float exposure.',
        'Fallback: books whose author has not onboarded still use the manual `Transfer.create` sweep, visible under the payouts queue.',
      ]},
      { h: 'Post-payment redirect security' },
      { p: 'The Stripe success/cancel URLs are locked to the frontend host in REACT_APP_BACKEND_URL. Attempts to steer the redirect elsewhere are rejected with 400.' },
    ],
  },
  {
    id: 'ai-models-budget',
    title: 'AI Models & Budget',
    icon: Coins,
    body: [
      { h: 'Available models' },
      { l: [
        'Claude Sonnet 4.5 (default), Claude Opus 4.7 — Anthropic',
        'GPT-5.2, GPT-5.4 — OpenAI',
        'Gemini 3 Flash, Gemini 3.1 Pro — Google',
        'All routed through the Emergent Universal Key. Enable/disable per model in the "AI Models" tab.',
      ]},
      { h: 'Global vs per-project budget' },
      { l: [
        'AI Budget tab → set `monthly_budget_default_usd` for the global cap.',
        'Per-project overrides go on `projects.monthly_budget_usd`.',
        'When the cap is hit, the user gets a paywall modal offering top-up or subscription.',
      ]},
      { h: 'Daily spend alert' },
      { p: 'Sidebar → LLM Cost & Balance → click the bell icon. Set a daily USD threshold and the card flashes red + toasts when today\'s COGS crosses it.' },
      { h: 'Auto-downgrade cost brake' },
      { p: 'Sidebar → LLM Cost & Balance → click the arrow-down icon. When enabled, every LLM call is silently rerouted to a cheaper fallback (default: Gemini 3 Flash) once today\'s COGS crosses the threshold. Resets automatically at UTC midnight. Users see a lower-cost generation, not an error. An "Downgrade ON" badge tells you when the brake is engaged.' },
    ],
  },
  {
    id: 'community',
    title: 'Community & Chat',
    icon: MessageSquare,
    body: [
      { h: 'Forum scopes' },
      { l: [
        '`community` — accessible to all The Loom / Premier users.',
        '`pro_house` — Premier Studio only; hidden from The Looms.',
        'Bans are per-user (not per-scope). Manage in the admin API `/admin/forum/bans`.',
      ]},
      { h: 'Real-time chat dock' },
      { p: 'Users get a persistent chat dock powered by Server-Sent Events. Capped at 4 concurrent SSE streams per user to prevent resource abuse.' },
      { h: '@Mentions' },
      { p: '`@handle` inside a post triggers an in-app notification badge for that user. Regex is `re.escape`-sanitised — Mongo-safe.' },
    ],
  },
  {
    id: 'bookstore',
    title: 'Bookstore & Marketplace',
    icon: Store,
    body: [
      { h: 'Bookstore admin' },
      { p: 'Sidebar → "Bookstore Admin" opens /bookstore/admin. Store Manager scope required.' },
      { h: 'Community Marketplace' },
      { p: 'Templates panel lets you approve/reject user-submitted templates. Starter Templates tab is the pre-seeded registry that seeds new user projects.' },
      { h: 'BYO Canva / Adobe (iter55z-f)' },
      { l: [
        'Canva Connect and Adobe Creative Cloud are now BYO — each user links their own account under Preferences → Connections.',
        'Refresh tokens are encrypted at rest with a Fernet key from the .env (FERNET_ENCRYPTION_KEY).',
        'Adobe PDF Services runs on an app-level Service Credential (does not charge the user\'s quota); CC Libraries + deep-links use their per-user OAuth token.',
        'Required env keys: CANVA_CLIENT_ID/SECRET, ADOBE_CLIENT_ID/SECRET, plus a Fernet key. Redirect URIs must match Canva / Adobe Developer Console entries exactly.',
      ]},
    ],
  },
  {
    id: 'accounting',
    title: 'Accounting',
    icon: BarChart3,
    body: [
      { h: 'Cost ledger' },
      { p: 'Accounting tab: revenue, COGS, gross/net profit. Grouped by day/week/month. Backed by the `cost_events` collection.' },
      { h: 'OPEX entries' },
      { p: 'Add monthly overhead (salaries, servers) so Net Profit reflects reality. `POST /admin/accounting/opex`.' },
      { h: 'Financial statements' },
      { p: 'Financials tab renders Income Statement, Balance Sheet, Cash Flow — driven by `journal_entries`. Editable per period.' },
    ],
  },
  {
    id: 'security',
    title: 'Security & Audit',
    icon: ShieldCheck,
    body: [
      { h: 'Admin password rotation' },
      { l: [
        'Use the self-service password reset flow; plaintext database overrides are disabled.',
        'Bootstrap fallback: `ADMIN_PASSWORD` env var.',
        'Test credentials are documented in `/app/memory/test_credentials.md`.',
      ]},
      { h: 'Cookies' },
      { p: 'Admin cookie is HttpOnly, Secure in production, SameSite=Lax by default. Set `COOKIE_SAMESITE=None` (with Secure) in .env for cross-origin production deployments.' },
      { h: 'Audit log' },
      { p: 'Every admin action writes to `admin_audit` with actor, target, and before/after diff where meaningful. Activity tab surfaces the last 500 events.' },
      { h: 'Rate limiting' },
      { p: '`bot_ratelimit` throttles login, password-reset, and generation endpoints. Buckets are per-IP with a token bucket refill.' },
    ],
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: LifeBuoy,
    body: [
      { h: 'Admin login bounces back to login' },
      { l: [
        'Likely a SameSite cookie problem on cross-origin production. Verify COOKIE_SAMESITE and Secure flags.',
        'Check the detail message on the login page — "Admin token required" means the cookie never arrived; "Admin session expired" means the timeout fired.',
      ]},
      { h: 'Production Cloud Build fails' },
      { p: 'Not a code error — Emergent orchestrator transient. Contact Emergent Support to clear the build cache.' },
      { h: 'Canva shows a placeholder' },
      { p: 'CANVA_CLIENT_ID / CANVA_CLIENT_SECRET are missing. Add them in .env and restart the backend.' },
      { h: 'Reports "LLM spend didn\'t change"' },
      { p: 'Refresh the sidebar card (Refresh icon). Data is aggregated on-demand from `cost_events`; there is no background job to wait for.' },
    ],
  },
];

function renderBlock(block, i) {
  if (block.h) {
    return (
      <h4
        key={i}
        className="mt-3 text-[13px] font-semibold text-foreground first:mt-0"
        data-testid="admin-guide-heading"
      >
        {block.h}
      </h4>
    );
  }
  if (block.p) {
    return (
      <p
        key={i}
        className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground"
        data-testid="admin-guide-para"
      >
        {block.p}
      </p>
    );
  }
  if (block.l) {
    return (
      <ul
        key={i}
        className="mt-1 space-y-1 text-[12.5px] leading-relaxed text-muted-foreground"
        data-testid="admin-guide-list"
      >
        {block.l.map((li, j) => (
          <li key={j} className="flex gap-1.5">
            <ChevronRight className="mt-1 h-3 w-3 shrink-0 text-muted-foreground/60" />
            <span>{li}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
}

function sectionMatchesQuery(section, q) {
  if (!q) return true;
  const needle = q.toLowerCase();
  if (section.title.toLowerCase().includes(needle)) return true;
  for (const b of section.body) {
    if (b.h && b.h.toLowerCase().includes(needle)) return true;
    if (b.p && b.p.toLowerCase().includes(needle)) return true;
    if (b.l && b.l.some((li) => li.toLowerCase().includes(needle))) return true;
  }
  return false;
}

export default function AdminGuidePanel() {
  const [q, setQ] = useState('');
  const [activeId, setActiveId] = useState(SECTIONS[0].id);

  const filtered = useMemo(
    () => SECTIONS.filter((s) => sectionMatchesQuery(s, q)),
    [q]
  );

  const active = filtered.find((s) => s.id === activeId) || filtered[0];

  return (
    <div className="space-y-3" data-testid="admin-guide-panel">
      <PanelHeader
        title="Admin Guide"
        subtitle="Everything you need to operate The Loom, the Bookstore, and the Marketplace."
      />

      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search the guide (e.g. tokens, Stripe, cookie)…"
          className="pl-8 text-[13px]"
          data-testid="admin-guide-search"
        />
      </div>

      <div className="grid grid-cols-[220px_1fr] gap-4">
        {/* Section list */}
        <nav
          className="rounded-md border border-border bg-muted/30 p-1"
          data-testid="admin-guide-nav"
        >
          {filtered.length === 0 && (
            <div className="p-2 text-[12px] text-muted-foreground">
              No sections match.
            </div>
          )}
          {filtered.map((s) => {
            const Icon = s.icon;
            const isActive = active && s.id === active.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveId(s.id)}
                className={`inline-flex h-8 w-full items-center gap-2 rounded-sm px-2 text-[12px] ${
                  isActive
                    ? 'bg-background font-semibold text-foreground'
                    : 'text-muted-foreground hover:bg-background/60'
                }`}
                data-testid={`admin-guide-nav-${s.id}`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                {s.title}
              </button>
            );
          })}
        </nav>

        {/* Active section body */}
        <article
          className="rounded-md border border-border bg-background p-4"
          data-testid="admin-guide-body"
        >
          {active ? (
            <>
              <h3 className="mb-2 flex items-center gap-2 text-[15px] font-semibold text-foreground">
                <active.icon className="h-4 w-4" />
                {active.title}
              </h3>
              {active.body.map((b, i) => renderBlock(b, i))}
              <div className="mt-4 flex items-center gap-1.5 rounded-sm bg-muted/40 px-2 py-1.5 text-[10.5px] text-muted-foreground">
                <Bell className="h-3 w-3" />
                Guide is versioned with the code. Ping the dev team if any info here is stale.
              </div>
            </>
          ) : (
            <div className="text-[13px] text-muted-foreground">
              Pick a section on the left.
            </div>
          )}
        </article>
      </div>
    </div>
  );
}
